import React, { useEffect, useRef, useState } from "react";
import { adminApi, adminUpload, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Image as ImageIcon, Upload, X } from "lucide-react";

const CATEGORIES = [
  "EV Batteries",
  "Solar Street Lights",
  "Industrial Batteries",
  "Custom Packs",
  "Inverters",
  "EV Vehicles",
  "Accessories",
  "Other",
];

const EMPTY = {
  name: "",
  category: CATEGORIES[0],
  short_description: "",
  long_description: "",
  image_url: "",
  gallery: [],
  price_text: "On request",
  price: "",
  mrp: "",
  in_stock: true,
  featured: false,
  specs: [],
  warranty: "5 years",
  rating: "",
  rating_count: "",
  badge: "",
};

export default function ProductsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const fileRef = useRef(null);
  const galleryRef = useRef(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updSpec = (i, k, v) =>
    setForm((f) => ({
      ...f,
      specs: f.specs.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)),
    }));

  const addSpec = () => upd("specs", [...form.specs, { label: "", value: "" }]);
  const removeSpec = (i) => upd("specs", form.specs.filter((_, idx) => idx !== i));

  const load = async () => {
    try {
      const r = await adminApi.get("/products");
      setItems(r.data || []);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminUpload("/admin/upload-image", file);
      upd("image_url", res.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleGalleryPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setGalleryUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const res = await adminUpload("/admin/upload-image", file);
        urls.push(res.url);
      }
      setForm((f) => ({ ...f, gallery: [...f.gallery, ...urls] }));
      toast.success(`${urls.length} image(s) added to gallery`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setGalleryUploading(false);
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const removeGalleryImage = (idx) =>
    setForm((f) => ({ ...f, gallery: f.gallery.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    const cleanedSpecs = form.specs.filter((s) => s.label.trim() && s.value.trim());
    const payload = {
      ...form,
      specs: cleanedSpecs,
      price: form.price !== "" ? Number(form.price) : null,
      mrp: form.mrp !== "" ? Number(form.mrp) : null,
      rating: form.rating !== "" ? Number(form.rating) : null,
      rating_count: form.rating_count !== "" ? Number(form.rating_count) : null,
    };
    setSaving(true);
    try {
      if (editingId) {
        await adminApi.put(`/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await adminApi.post("/products", payload);
        toast.success("Product added");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      category: p.category || CATEGORIES[0],
      short_description: p.short_description || "",
      long_description: p.long_description || "",
      image_url: p.image_url || "",
      gallery: p.gallery || [],
      price_text: p.price_text || "",
      price: p.price != null ? String(p.price) : "",
      mrp: p.mrp != null ? String(p.mrp) : "",
      in_stock: !!p.in_stock,
      featured: !!p.featured,
      specs: p.specs || [],
      warranty: p.warranty || "",
      rating: p.rating != null ? String(p.rating) : "",
      rating_count: p.rating_count != null ? String(p.rating_count) : "",
      badge: p.badge || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await adminApi.delete(`/products/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-10" data-testid="admin-products-tab">
      <form onSubmit={submit} className="industrial-card p-6 md:p-8 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl uppercase tracking-tight">
            {editingId ? "Edit product" : "New product"}
          </h3>
          <Plus size={18} className="text-[#e63946]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="label-mono">Name *</label>
            <input className="vps-input" value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="admin-product-name" />
          </div>
          <div>
            <label className="label-mono">Category *</label>
            <select className="vps-input" value={form.category} onChange={(e) => upd("category", e.target.value)} required data-testid="admin-product-category">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono">Price text (fallback)</label>
            <input className="vps-input" value={form.price_text} onChange={(e) => upd("price_text", e.target.value)} placeholder="On request" data-testid="admin-product-price" />
          </div>
          <div>
            <label className="label-mono">Price (₹, numeric)</label>
            <input type="number" min="0" className="vps-input" value={form.price} onChange={(e) => upd("price", e.target.value)} placeholder="e.g. 5500" data-testid="admin-product-price-num" />
          </div>
          <div>
            <label className="label-mono">MRP (₹, for strike-through)</label>
            <input type="number" min="0" className="vps-input" value={form.mrp} onChange={(e) => upd("mrp", e.target.value)} placeholder="e.g. 7500" data-testid="admin-product-mrp" />
          </div>
          <div>
            <label className="label-mono">Warranty</label>
            <input className="vps-input" value={form.warranty} onChange={(e) => upd("warranty", e.target.value)} placeholder="5 years" data-testid="admin-product-warranty" />
          </div>
          <div>
            <label className="label-mono">Badge (optional)</label>
            <input className="vps-input" value={form.badge} onChange={(e) => upd("badge", e.target.value)} placeholder="e.g. Best Seller, New" data-testid="admin-product-badge" />
          </div>
          <div>
            <label className="label-mono">Rating (0-5)</label>
            <input type="number" step="0.1" min="0" max="5" className="vps-input" value={form.rating} onChange={(e) => upd("rating", e.target.value)} placeholder="e.g. 4.7" data-testid="admin-product-rating" />
          </div>
          <div>
            <label className="label-mono">Rating count</label>
            <input type="number" min="0" className="vps-input" value={form.rating_count} onChange={(e) => upd("rating_count", e.target.value)} placeholder="e.g. 128" data-testid="admin-product-rating-count" />
          </div>
          <div className="md:col-span-2">
            <label className="label-mono">Short description *</label>
            <input className="vps-input" value={form.short_description} onChange={(e) => upd("short_description", e.target.value)} required maxLength={400} data-testid="admin-product-short" />
          </div>
          <div className="md:col-span-2">
            <label className="label-mono">Long description</label>
            <textarea className="vps-input" rows={5} value={form.long_description} onChange={(e) => upd("long_description", e.target.value)} data-testid="admin-product-long" />
          </div>
          <div className="md:col-span-2">
            <label className="label-mono">Image — upload OR paste URL</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="file"
                accept="image/*"
                ref={fileRef}
                onChange={handleImagePick}
                className="hidden"
                data-testid="admin-product-image-file"
              />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-secondary" data-testid="admin-product-image-upload">
                <Upload size={14} /> {uploading ? "Uploading…" : "Upload image"}
              </button>
              <div className="flex-1 flex items-center gap-2">
                <ImageIcon size={14} className="text-zinc-500" />
                <input className="vps-input" value={form.image_url} onChange={(e) => upd("image_url", e.target.value)} placeholder="…or paste image URL" data-testid="admin-product-image-url" />
              </div>
            </div>
            {form.image_url && (
              <img src={fileUrl(form.image_url)} alt="preview" className="mt-3 h-24 object-cover border border-white/10" data-testid="admin-product-image-preview" />
            )}
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <label className="label-mono mb-0">Gallery — additional images</label>
              <input
                type="file"
                accept="image/*"
                multiple
                ref={galleryRef}
                onChange={handleGalleryPick}
                className="hidden"
                data-testid="admin-product-gallery-file"
              />
              <button
                type="button"
                onClick={() => galleryRef.current?.click()}
                disabled={galleryUploading}
                className="text-xs font-mono uppercase tracking-[0.2em] text-[#e63946] hover:underline inline-flex items-center gap-1"
                data-testid="admin-product-gallery-upload"
              >
                <Upload size={12} /> {galleryUploading ? "Uploading…" : "+ Add images"}
              </button>
            </div>
            {form.gallery.length > 0 ? (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="admin-product-gallery-grid">
                {form.gallery.map((g, i) => (
                  <div key={i} className="relative group aspect-square border border-white/10 overflow-hidden">
                    <img src={fileUrl(g)} alt={`gallery ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(i)}
                      className="absolute top-1 right-1 bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove image"
                      data-testid={`admin-product-gallery-remove-${i}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-zinc-500 font-mono uppercase tracking-[0.2em]">
                No gallery images yet
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <label className="label-mono mb-0">Specifications</label>
              <button type="button" onClick={addSpec} className="text-xs font-mono uppercase tracking-[0.2em] text-[#e63946] hover:underline" data-testid="admin-product-add-spec">
                + Add spec
              </button>
            </div>
            <div className="space-y-2">
              {form.specs.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2" data-testid={`admin-product-spec-${i}`}>
                  <input className="vps-input col-span-4" placeholder="Label (e.g. Voltage)" value={s.label} onChange={(e) => updSpec(i, "label", e.target.value)} />
                  <input className="vps-input col-span-7" placeholder="Value (e.g. 48V)" value={s.value} onChange={(e) => updSpec(i, "value", e.target.value)} />
                  <button type="button" onClick={() => removeSpec(i)} className="col-span-1 text-zinc-500 hover:text-[#e63946]" aria-label="Remove spec">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {form.specs.length === 0 && (
                <div className="text-xs text-zinc-500 font-mono uppercase tracking-[0.2em]">
                  No specs added yet
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="admin-product-instock-label">
              <input type="checkbox" checked={form.in_stock} onChange={(e) => upd("in_stock", e.target.checked)} />
              In stock
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="admin-product-featured-label">
              <input type="checkbox" checked={form.featured} onChange={(e) => upd("featured", e.target.checked)} />
              Featured
            </label>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving} className="btn-primary" data-testid="admin-product-submit">
            {saving ? "Saving…" : editingId ? "Save changes" : "Add product"}
          </button>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="btn-secondary" data-testid="admin-product-cancel-edit">
              Cancel edit
            </button>
          )}
        </div>
      </form>

      <div>
        <h3 className="font-display text-xl uppercase tracking-tight mb-5">
          Published products ({items.length})
        </h3>
        {items.length === 0 ? (
          <div className="text-zinc-500 text-sm">No products yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((p) => (
              <div key={p.id} className="industrial-card p-5" data-testid={`admin-product-card-${p.id}`}>
                {p.image_url && <img src={fileUrl(p.image_url)} alt={p.name} className="w-full h-28 object-cover border border-white/10 mb-3" />}
                <div className="tag-chip tag-chip-red">{p.category}</div>
                <div className="font-display text-base uppercase mt-2 leading-tight">{p.name}</div>
                <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em] mt-2">
                  {p.price_text} · {p.in_stock ? "In stock" : "MTO"}{p.featured ? " · Featured" : ""}
                </div>
                <button onClick={() => handleEdit(p)} className="mt-4 mr-3 text-xs font-mono uppercase tracking-[0.2em] text-zinc-300 hover:text-[#e63946] inline-flex items-center gap-1" data-testid={`admin-product-edit-${p.id}`}>
                  <Plus size={12} className="rotate-45" /> Edit
                </button>
                <button onClick={() => handleDelete(p.id)} className="mt-4 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 hover:text-[#e63946] inline-flex items-center gap-1" data-testid={`admin-product-delete-${p.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
